import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Row, Col, ListGroup, Image, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import CheckoutSteps from '../components/CheckoutSteps';
import { createOrder } from '../actions/orderActions';
import { savePaymentMethod } from '../actions/cartActions';
import { ORDER_CREATE_RESET } from '../constants/orderConstants';
import { useTranslation } from 'react-i18next';
import { BOOK_PRODUCT_ID, PRINT_PRODUCT_ID } from '../constants/productConfig';
import { usePricing } from '../hooks/usePricing';
import { getVatRateByCountry } from '../constants/vatConfig';

function PlaceOrderScreen() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { shippingPhysicalPrice } = usePricing();
    
    const cart = useSelector(state => state.cart);
    const { cartItems, shippingAddress, paymentMethod, customization, voucherCode, discountAmount, shippingDiscountAmount } = cart;

    const orderCreate = useSelector(state => state.orderCreate);
    const { order, error, success } = orderCreate;

    const isPhysicalCartItem = (item) => (
        Number(item.product) === BOOK_PRODUCT_ID ||
        Number(item.product) === PRINT_PRODUCT_ID ||
        !item.is_digital
    );

    const formatShippingAddress = (addressObj = {}) => {
        const addressLine = addressObj.address || addressObj.Address || '';
        const city = addressObj.city || addressObj.City || '';
        const postalCode = addressObj.postalCode || addressObj.postal_code || addressObj.postal || '';
        const country = addressObj.country || addressObj.Country || '';
        const phoneNumber = addressObj.phoneNumber || addressObj.phone_number || '';

        const cityPostal = [city, postalCode].filter(Boolean).join(' ');
        return [addressLine, cityPostal, country, phoneNumber].filter(Boolean).join(', ');
    };

    // Calculate prices
    const addDecimals = (num) => {
        return (Math.round(num * 100) / 100).toFixed(2);
    };

    cart.itemsPrice = addDecimals(
        cart.cartItems.reduce((acc, item) => acc + parseFloat(item.price) * item.qty, 0)
    );

    // Shipping: Free for digital products only; physical shipping uses configured price
    const hasPhysicalItems = cart.cartItems.some(item => isPhysicalCartItem(item));
    cart.shippingPrice = addDecimals(hasPhysicalItems ? shippingPhysicalPrice : 0);

    // Discount from voucher (item + shipping)
    const itemDiscount = Number(discountAmount || 0);
    const shippingDiscount = Number(shippingDiscountAmount || 0);
    cart.discountAmount = itemDiscount + shippingDiscount;

    // Tax: country-specific VAT on the post-discount total (included in price, shown separately)
    const preTaxTotal = Math.max(0, Number(cart.itemsPrice) + Number(cart.shippingPrice) - Number(cart.discountAmount));
    const vatRate = getVatRateByCountry(shippingAddress?.country);
    cart.taxPrice = addDecimals(Number((vatRate * preTaxTotal).toFixed(2)));

    // Total (subtract discount; tax is already included)
    cart.totalPrice = preTaxTotal.toFixed(2);

    // Redirect if cart is empty
    useEffect(() => {
        if (cartItems.length === 0) {
            navigate('/cart');
        }
    }, [navigate, cartItems]);

    useEffect(() => {
        if (!paymentMethod) {
            dispatch(savePaymentMethod('PayPal'));
        }
    }, [dispatch, paymentMethod]);

    useEffect(() => {
        if (success) {
            localStorage.setItem('lastOrderId', String(order.OrderId));
            navigate(`/order/${order.OrderId}`);
            dispatch({ type: ORDER_CREATE_RESET });
        }
    }, [success, navigate, order, dispatch]);

    const placeOrderHandler = () => {
        dispatch(createOrder({
            orderItems: cart.cartItems,
            shippingAddress: cart.shippingAddress,
            paymentMethod: cart.paymentMethod || 'PayPal',
            customization: cart.customization,
            voucherCode: voucherCode || ''  // Backend computes all monetary fields
        }));
    };

    return (
        <div>
            <CheckoutSteps step1 step2 step3 step4 />
            {error && <Message variant='danger'>{error}</Message>}
            <Row>
                <Col md={8}>
                    <ListGroup variant='flush'>
                        <ListGroup.Item>
                            <h2>{t('placeOrder.shipping')}</h2>
                            <p>
                                <strong>{t('placeOrder.address')} </strong>
                                {!hasPhysicalItems
                                    ? t('placeOrder.digitalDelivery')
                                    : (formatShippingAddress(shippingAddress) || '-')
                                }
                            </p>
                        </ListGroup.Item>

                        <ListGroup.Item>
                            <h2>{t('placeOrder.paymentMethod')}</h2>
                            <p>
                                <strong>{t('placeOrder.method')} </strong>
                                {paymentMethod || 'PayPal'}
                            </p>
                        </ListGroup.Item>

                        {customization && !customization.skipped && (
                            <ListGroup.Item>
                                <h2>{t('placeOrder.bookCustomization')}</h2>
                                {customization.childName && (
                                    <p><strong>{t('placeOrder.childName')} </strong>{customization.childName}</p>
                                )}
                                {customization.childAge && (
                                    <p><strong>{t('placeOrder.age')} </strong>{customization.childAge}</p>
                                )}
                                {customization.storyTheme && (
                                    <p><strong>{t('placeOrder.theme')} </strong>{customization.storyTheme}</p>
                                )}
                            </ListGroup.Item>
                        )}

                        <ListGroup.Item>
                            <h2>{t('placeOrder.orderItems')}</h2>
                            {cartItems.length === 0 ? (
                                <Message variant='info'>{t('placeOrder.cartEmpty')}</Message>
                            ) : (
                                <ListGroup variant='flush'>
                                    {cartItems.map((item, index) => (
                                        <ListGroup.Item key={index}>
                                            <Row>
                                                <Col md={1}>
                                                    <Image src={item.image} alt={item.name} fluid rounded />
                                                </Col>
                                                <Col>
                                                    {item.name}
                                                </Col>
                                                <Col md={4}>
                                                    {item.qty} x {item.price} {t('common.dkk')} = {(item.qty * item.price).toFixed(2)} {t('common.dkk')}
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </ListGroup.Item>
                    </ListGroup>
                </Col>

                <Col md={4}>
                    <Card>
                        <ListGroup variant='flush'>
                            <ListGroup.Item>
                                <h2>{t('placeOrder.orderSummary')}</h2>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('placeOrder.items')}</Col>
                                    <Col>{cart.itemsPrice} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('placeOrder.shippingCost')}</Col>
                                    <Col>{cart.shippingPrice} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('placeOrder.tax')}</Col>
                                    <Col>{cart.taxPrice} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>
                            
                            {cart.discountAmount > 0 && (
                                <ListGroup.Item>
                                    <Row>
                                        <Col>{t('placeOrder.discount', { code: voucherCode })}:</Col>
                                        <Col className='text-success'>-{cart.discountAmount.toFixed(2)} {t('common.dkk')}</Col>
                                    </Row>
                                </ListGroup.Item>
                            )}

                            <ListGroup.Item>
                                <Row>
                                    <Col><strong>{t('placeOrder.total')}</strong></Col>
                                    <Col><strong>{cart.totalPrice} {t('common.dkk')}</strong></Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Button
                                    type='button'
                                    className='w-100 mb-2'
                                    disabled={cartItems.length === 0}
                                    onClick={placeOrderHandler}
                                >
                                    {t('placeOrder.submit')}
                                </Button>
                                <small className='text-muted d-block text-center' style={{ fontSize: '0.78rem' }}>
                                    By placing your order, you agree to TaleTots' <a href='/privacy' target='_blank' rel='noopener noreferrer' style={{ color: '#667eea', textDecoration: 'underline' }}>Privacy Policy</a>. An official PDF copy will be attached to your order confirmation email.
                                </small>
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default PlaceOrderScreen;
